import React, { useState, useEffect, useRef } from 'react';
import { Users, Home, LogOut, UserCheck, Menu, X, Mail, PawPrint, LayoutGrid, Briefcase, Package, Trash2, AlertCircle, Calendar, Milk, ChevronDown, Bell, ChevronLeft, ChevronRight, CreditCard, Cpu, ShieldCheck, ClipboardList, ArrowLeftRight, CirclePlus, UserPlus, TrendingUp, MapPin, Wallet, BarChart2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  toggleSidebar,
  setSidebarOpen,
  setShowAdminDetails,
  setSnackbar,
} from '../../store/slices/uiSlice';
import { markAllRead, setUnreadCount, incrementUnreadCount } from '../../store/slices/wsSlice';
import { farmvestService } from '../../services/farmvest_api';
import { fetchTickets } from '../../store/slices/farmvest/tickets';
import { fetchAdminProfile } from '../../store/slices/usersSlice';

import ImageNamesModal from '../modals/ImageNamesModal';
import AdminDetailsModal from '../modals/AdminDetailsModal';
import RejectionModal from '../modals/RejectionModal';
import ApprovalModal from '../modals/ApprovalModal';
import LogoutModal from '../modals/LogoutModal';
import Snackbar from '../common/Snackbar';
import { useScreenAccess } from '../../hooks/useScreenAccess';
import { requestNotificationPermission } from '../../utils/notificationUtility';
import { onMessage } from 'firebase/messaging';
import { messaging } from '../../config/firebaseAppConfig';
import NotificationSidebar from '../common/NotificationSidebar';
import TicketNotificationToast from '../common/TicketNotificationToast';
import { fireTicketNotification } from '../../utils/ticketNotificationManager';

interface UserTabsProps {
  adminMobile?: string;
  adminName?: string;
  adminRole?: string;
  lastLogin?: string;
  presentLogin?: string;
  locationName?: string;
  onLogout?: () => void;
  children: React.ReactNode;
}

// Tailwind Class Constants for Navigation Items
const NAV_ITEM_BASE = "nav-item flex items-center w-full py-[11px] px-4 border-none bg-transparent rounded-[10px] text-slate-300 text-[0.8125rem] font-medium cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] text-left outline-none relative my-0.5 hover:bg-white/8 hover:text-white 4xl:py-8 4xl:px-10 4xl:text-[1.8rem] 4xl:rounded-[24px] 4xl:my-3";

const NAV_ITEM_ACTIVE = "bg-gradient-to-br from-[#f59e0b] to-[#d97706] !text-white font-bold shadow-[0_4px_12px_-2px_rgba(245,158,11,0.35)]";

const NAV_ITEM_COLLAPSED = "md:justify-center md:p-3 md:w-11 md:h-11 md:mx-auto md:rounded-xl after:content-[attr(data-label)] after:fixed after:left-[70px] after:top-[var(--tooltip-top)] after:-translate-y-1/2 after:bg-[#1e293b] after:bg-gradient-to-br after:from-[#1e293b] after:to-[#334155] after:text-white after:text-[0.75rem] after:font-medium after:px-3 after:py-1.5 after:rounded-md after:whitespace-nowrap after:z-[10001] after:pointer-events-none after:shadow-2xl after:opacity-0 hover:after:opacity-100 after:transition-all after:duration-300 after:scale-95 hover:after:scale-100 before:content-[''] before:fixed before:left-[62px] before:top-[var(--tooltip-top)] before:-translate-y-1/2 before:border-[5px] before:border-transparent before:border-r-[#1e293b] before:z-[10001] before:pointer-events-none before:opacity-0 hover:before:opacity-100 before:transition-all before:duration-300 4xl:after:left-[160px] 4xl:after:text-[1.4rem] 4xl:after:px-6 4xl:after:py-3 4xl:after:rounded-xl 4xl:before:left-[148px] 4xl:before:border-[8px]";


/** Hides a nav item when the user has no view access to that screen. */
const NavGuard = ({ screenKey, children }: { screenKey: string; children: React.ReactNode }) => {
  const { canView } = useScreenAccess(screenKey);
  if (!canView) return null;
  return <>{children}</>;
};

const UserTabs: React.FC<UserTabsProps> = ({ adminMobile, adminName, adminRole, lastLogin, presentLogin, locationName, onLogout, children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Local State
  const [displayAdminName, setDisplayAdminName] = useState(adminName);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isTicketsSubmenuOpen, setIsTicketsSubmenuOpen] = useState(false);
  const [isNotificationSidebarOpen, setIsNotificationSidebarOpen] = useState(false);


  // UI State from Redux
  const { isSidebarOpen, snackbar } = useAppSelector((state: RootState) => state.ui);
  const unreadCount = useAppSelector((state: RootState) => state.ws.unreadCount);
  const ticketRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine active tab
  const currentPath = location.pathname;
  let activeTab = 'farmvest-employees';
  if (currentPath.includes('/farmvest/company-overview')) activeTab = 'company-overview';
  else if (currentPath.includes('/farmvest/dashboard')) activeTab = 'farmvest-dashboard';
  else if (currentPath.includes('/farmvest/farm-visit')) activeTab = 'farmvest-farm-visit';
  else if (currentPath.includes('/farmvest/employees')) activeTab = 'farmvest-employees';
  else if (currentPath.includes('/farmvest/farms')) activeTab = 'farmvest-farms';
  else if (currentPath.includes('/farmvest/user-activation')) activeTab = 'farmvest-activation';
  else if (currentPath.includes('/farmvest/animal-onboarding')) activeTab = 'animal-onboarding';
  // The double-s route (/unallocated-animalss) is the post-onboarding allocation screen reached
  // mid-onboarding, so it keeps the Animal Onboarding tab active. Checked BEFORE the single-s
  // route because '/farmvest/unallocated-animals' is a substring of '/farmvest/unallocated-animalss'.
  else if (currentPath.includes('/farmvest/unallocated-animalss')) activeTab = 'animal-onboarding';
  else if (currentPath.includes('/farmvest/unallocated-animals')) activeTab = 'unallocated-animals';
  else if (currentPath.includes('/farmvest/investors')) activeTab = 'farmvest-investors';
  else if (currentPath.includes('/farmvest/inventory')) activeTab = 'farmvest-inventory';
  else if (currentPath.includes('/farmvest/buffalo')) activeTab = 'farmvest-buffalo';
  else if (currentPath.includes('/farmvest/tickets')) activeTab = 'farmvest-tickets';
  else if (currentPath.includes('/farmvest/leave-requests')) activeTab = 'farmvest-leave';
  else if (currentPath.includes('/farmvest/unit-transfer-requests')) activeTab = 'farmvest-unit-transfers';
  else if (currentPath.includes('/farmvest/alerts')) activeTab = 'farmvest-alerts';
  else if (currentPath.includes('/farmvest/iot-alerts')) activeTab = 'iot-alerts';
  else if (currentPath.includes('/farmvest/milk-production')) activeTab = 'farmvest-milk';
  else if (currentPath.includes('/support-tickets')) activeTab = 'support-tickets';
  else if (currentPath.includes('/privacy-policy')) activeTab = 'privacy';
  else if (currentPath.includes('/support')) activeTab = 'support';
  // else if (currentPath.includes('/farmvest/onboarding-process')) activeTab = 'onboarding-process';
  else if (currentPath.includes('/farmvest/account-deletion')) activeTab = 'account-deletion';
  else if (currentPath.includes('/farmvest/due-payouts')) activeTab = 'farmvest-due-payouts';
  else if (currentPath.includes('/farmvest/wallet')) activeTab = 'farmvest-wallet';
  else if (currentPath.includes('/farmvest/payments')) activeTab = 'farmvest-payments';

  else if (currentPath.includes('/farmvest/roster')) activeTab = 'farmvest-roster';
  else if (currentPath.includes('/farmvest/investor-onboarding')) activeTab = 'investor-onboarding';

  // Auto-open submenu if tickets is active
  useEffect(() => {
    if (activeTab === 'farmvest-tickets') {
      setIsTicketsSubmenuOpen(true);
    }
  }, [activeTab]);

  // Synchronize OS Application icon badge with unread count
  useEffect(() => {
    const nav = navigator as any;
    if ('setAppBadge' in nav) {
      if (unreadCount > 0) {
        nav.setAppBadge(unreadCount).catch(() => { });
      } else {
        nav.clearAppBadge().catch(() => { });
      }
    }
  }, [unreadCount]);

  // Fetch initial unread notification count on mount
  useEffect(() => {
    farmvestService.getNotificationsUnreadCount()
      .then((res) => {
        if (res?.status === 'success') {
          dispatch(setUnreadCount(res.unread_count ?? 0));
        }
      })
      .catch(() => {/* silent */ });
  }, []);




  useEffect(() => {
    if (window.innerWidth <= 768) {
      dispatch(setSidebarOpen(false));
    } else {
      dispatch(setSidebarOpen(true));
    }

    const handleResize = () => {
      if (window.innerWidth <= 768) {
        dispatch(setSidebarOpen(false));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    navigate(path);
    if (window.innerWidth <= 768) {
      dispatch(setSidebarOpen(false));
    }
  };

  useEffect(() => {
    requestNotificationPermission();

    // Dedup by FCM message ID — same message can arrive on both onMessage (foreground)
    // and the SW postMessage path. Different messages always get through, even if rapid.
    const seenIds = new Set<string>();
    const markSeen = (id: string) => {
      seenIds.add(id);
      setTimeout(() => seenIds.delete(id), 10_000);
    };

    const handleFcmPayload = (
      rawData: Record<string, string>,
      notification?: { title?: string; body?: string },
      messageId?: string,
    ) => {
      // Fall back to a 300ms time bucket when there's no message ID so the two
      // delivery paths don't double-toast, but rapid legitimate messages still show.
      const dedupKey = messageId || `ts-${Math.floor(Date.now() / 300)}`;
      if (seenIds.has(dedupKey)) return;
      markSeen(dedupKey);

      const title = notification?.title || rawData['title'] || 'New Notification';
      const body = notification?.body || rawData['body'] || 'You have a new update on your dashboard.';
      const notifType = (rawData['notification_type'] || rawData['alert_type'] || '').toUpperCase();
      const isPayment = notifType === 'PAYMENT' || notifType === 'CPF_PAYMENT';
      const ticketType = isPayment
        ? 'PAYMENT'
        : (rawData['ticket_type'] || notifType.replace('TICKET_', '') || 'HEALTH');

      fireTicketNotification({
        title,
        body,
        ticketType,
        ticketId: rawData['reference_id'] ? Number(rawData['reference_id']) : undefined,
      });

      dispatch(incrementUnreadCount());

      if (ticketRefreshTimer.current) clearTimeout(ticketRefreshTimer.current);
      ticketRefreshTimer.current = setTimeout(() => {
        dispatch(fetchTickets());
        ticketRefreshTimer.current = null;
      }, 1000);
    };

    // ── Path 1: FCM modular onMessage (fires when tab is visible) ─────────────
    if (!messaging) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('[FCM] onMessage (foreground):', payload);
      handleFcmPayload(payload.data || {}, payload.notification, payload.messageId);
    });

    // ── Path 2: SW postMessage fallback ───────────────────────────────────────
    // The compat service worker can consume the FCM message before onMessage fires.
    // The SW now forwards a postMessage to the visible client as a guaranteed path.
    const swMessageHandler = (event: MessageEvent) => {
      if (event.data?.type !== 'FCM_NOTIFICATION') return;
      console.log('[FCM] SW postMessage (fallback):', event.data.payload);
      const p = event.data.payload;
      handleFcmPayload(p.data || {}, p.notification, p.messageId);
    };
    navigator.serviceWorker?.addEventListener('message', swMessageHandler);

    return () => {
      unsubscribe();
      navigator.serviceWorker?.removeEventListener('message', swMessageHandler);
      if (ticketRefreshTimer.current) clearTimeout(ticketRefreshTimer.current);
    };
  }, [navigate, dispatch]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchTickets());
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [dispatch]);

  const { adminProfile } = useAppSelector((state: RootState) => state.users);

  const [profileImageSrc, setProfileImageSrc] = useState<string | null>(null);

  // Eagerly fetch admin profile on mount if it's not yet in the store.
  // This ensures the profile image loads on first page load without waiting
  // for App.tsx's effect cycle to complete.
  useEffect(() => {
    if (!adminProfile && adminMobile) {
      dispatch(fetchAdminProfile(adminMobile));
    }
  }, [adminMobile]);

  useEffect(() => {
    if (adminProfile) {
      const user = adminProfile;
      let fullName = '';
      if (user.first_name || user.last_name) {
        fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      } else if (user.name) {
        fullName = user.name;
      }
      if (fullName) setDisplayAdminName(fullName);

      if (user.profile_image_url) {
        setProfileImageSrc(user.profile_image_url);
      } else {
        const empId = user.employee_id || user.id || user.user_id;
        if (empId) {
          farmvestService.getEmployeeDetailsById(String(empId))
            .then(res => {
              const img = (res.data || res)?.profile_image_url;
              if (img) setProfileImageSrc(img);
            })
            .catch(() => { /* Silent fail */ });
        }
      }
    }
  }, [adminProfile, adminMobile]);

  const hasSession = !!adminMobile;



  const sidebarRef = React.useRef<HTMLElement>(null);

  // Restore scroll position on mount
  useEffect(() => {
    const savedScrollPos = sessionStorage.getItem('sidebar-scroll-pos');
    if (savedScrollPos && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }
  }, []);

  const handleSidebarScroll = (e: React.UIEvent<HTMLElement>) => {
    sessionStorage.setItem('sidebar-scroll-pos', e.currentTarget.scrollTop.toString());
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-[#f8fafc] font-sans text-[#0f172a] overflow-hidden">
      <div className={`fixed inset-0 bg-black/50 z-[99] transition-opacity duration-300 pointer-events-none md:hidden ${isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`} onClick={() => dispatch(setSidebarOpen(false))} />

      {hasSession && (
        <header className="bg-[#113025] backdrop-blur-[10px] h-[56px] px-4 flex items-center justify-between shadow-[0_1px_0_0_rgba(255,255,255,0.1)] z-[1000] text-white sticky top-0 4xl:h-[200px] 4xl:px-[120px]">

          {/* Left: Hamburger + Dynamic Title */}
          <div className="flex items-center gap-2 min-[400px]:gap-4 min-w-0 flex-1">
            <button
              className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
              onClick={() => dispatch(toggleSidebar())}
            >
              <Menu size={24} className="4xl:w-16 4xl:h-16" />
            </button>

            <div className="flex items-center gap-1.5 min-[400px]:gap-3 4xl:gap-8 overflow-hidden">
              <div className="flex-shrink-0">
                <img src="/farmvest-logo.png" alt="Logo" className="h-7 min-[400px]:h-8 md:h-9 4xl:h-20 w-auto" />
              </div>

              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <h1 className="text-xs min-[400px]:text-sm min-[500px]:text-lg font-bold text-white leading-tight 4xl:text-5xl truncate">
                  Farmvest Global Pvt Ltd
                </h1>
                {locationName && (
                  <div className="flex items-center gap-1.5 border-l border-white/20 pl-2 md:pl-3 ml-0.5 mt-0.5 md:mt-1">
                    <MapPin size={15} className="text-emerald-400 4xl:w-7 4xl:h-6" />
                    <span className="text-[12px] md:text-[15px] font-black text-emerald-400 tracking-wider uppercase whitespace-nowrap">
                      {locationName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Status + Profile */}
          <div className="flex items-center flex-shrink-0 ml-2">
            {/* {API_CONFIG.getEnvironment() === 'dev' && (
              <div className="status-pill bg-amber-100 border border-amber-200 px-4 py-1.5 rounded-full flex items-center gap-2 mr-3">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-600 animate-pulse"></div>
                <span className="text-sm font-bold text-amber-700">Dev_mode</span>
              </div>
            )} */}
            <div className="bg-green-100/15 border border-green-200/30 px-4 py-1.5 rounded-full flex items-center gap-2 4xl:px-[56px] 4xl:py-[32px] 4xl:gap-[24px]">
              <div className="w-2.5 h-2.5 4xl:w-6 4xl:h-6 rounded-full bg-green-600 animate-pulse"></div>
              <span className="text-sm font-bold text-green-700 4xl:text-3xl">Online</span>
            </div>


            <button
              onClick={() => setIsNotificationSidebarOpen(true)}
              className="p-2 ml-4 4xl:ml-12 text-white hover:text-[#fbbf24] transition-all transform hover:scale-110 active:scale-95 relative flex items-center justify-center bg-white/10 rounded-full border border-white/20 4xl:p-6"
              title="View Notifications"
            >
              <Bell size={18} fill="currentColor" strokeWidth={2.5} className="4xl:w-12 4xl:h-12" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 4xl:min-w-[36px] 4xl:h-[36px] 4xl:text-xl">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            <div
              className="flex items-center gap-3 cursor-pointer ml-4 pl-4 4xl:ml-12 4xl:pl-12 border-l border-white/20 text-white hover:opacity-80 transition-opacity 4xl:gap-8"
              onClick={() => dispatch(setShowAdminDetails(true))}
            >
              <div className="text-right hidden md:block min-w-0 max-w-[150px] lg:max-w-[250px]">
                <p className="text-sm font-bold text-white 4xl:text-[2.8rem] truncate">{displayAdminName || 'Admin User'}</p>
                <p className="text-xs text-gray-300 4xl:text-[1.8rem] truncate">{adminRole || 'Administrator'}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f59e0b] to-[#d97706] border-2 border-white/30 shadow-[0_0_15px_rgba(37,99,235,0.3)] overflow-hidden flex items-center justify-center text-white font-bold relative 4xl:w-[140px] 4xl:h-[140px] 4xl:text-[4rem] 4xl:border-[5px]">
                {/* Fallback Initials */}
                <span className="absolute inset-0 flex items-center justify-center z-0">
                  {displayAdminName ? displayAdminName.substring(0, 2).toUpperCase() : 'AD'}
                </span>

                {/* Actual Image if available */}
                {profileImageSrc && (
                  <img
                    src={profileImageSrc}
                    alt="Profile"
                    className="w-full h-full object-cover relative z-10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>

            </div>
          </div>
        </header>
      )}



      <div className="flex flex-row flex-1 overflow-hidden relative min-h-0">
        <nav
          ref={sidebarRef}
          className={`flex flex-col shrink-0 transition-[width,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] whitespace-nowrap overflow-y-auto overflow-x-hidden relative z-[100] text-[#f8fafc] border-r border-white/10 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden !bg-[#113025] overscroll-contain
            max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:h-[100dvh] max-md:z-[2000] max-md:shadow-[4px_0_15px_rgba(0,0,0,0.3)] max-md:pb-6
            ${isSidebarOpen ? 'w-[230px] 4xl:w-[550px] max-md:w-[250px] translate-x-0' : 'w-[60px] 4xl:w-[140px] max-md:w-[250px] max-md:-translate-x-full md:p-0'}`}
          onScroll={handleSidebarScroll}
        >


          <div className="hidden max-md:flex items-center justify-center h-[60px] shrink-0 px-4 border-b border-white/10 bg-[#113025] relative box-border 4xl:h-[160px] 4xl:px-[40px] 4xl:gap-6">
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center p-2 text-white/80 hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); dispatch(setSidebarOpen(false)); }}
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-2 pl-8 overflow-hidden max-w-full">
              <img src="/farmvest-logo.png" alt="farmvest Logo" className="h-8 w-auto flex-none contrast-[1.2] 4xl:h-[100px]" />
              <span className="text-white font-[800] tracking-tight truncate text-sm min-[400px]:text-base 4xl:text-[3rem]">
                Farmvest Global Pvt Ltd
              </span>
            </div>
          </div>
          <ul
            className="list-none px-3 my-2.5 flex flex-col gap-1 max-md:px-4 4xl:px-8 4xl:gap-4 4xl:mt-5"
            onMouseOver={(e) => {
              if (isSidebarOpen) return;
              const item = (e.target as HTMLElement).closest('.nav-item') as HTMLElement;
              if (item) {
                const rect = item.getBoundingClientRect();
                item.style.setProperty('--tooltip-top', `${rect.top + rect.height / 2}px`);
              }
            }}
          >

            {/* <NavGuard screenKey="DASHBOARD">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-dashboard' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Dashboard" onClick={(e) => { handleNavClick(e, '/farmvest/dashboard'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <LayoutGrid size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Dashboard</span>
                  </div>
                </button>
              </li>
            </NavGuard> */}

            <NavGuard screenKey="DASHBOARD">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-dashboard' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Dashboard" onClick={(e) => { handleNavClick(e, '/farmvest/dashboard'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <BarChart2 size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Dashboard</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="FARMS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-farms' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Farm Management" onClick={(e) => { handleNavClick(e, '/farmvest/farms'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Home size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Farm Management</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="ROSTER">
              <li>
                <button
                  className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-roster' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`}
                  data-label="Roster"
                  onClick={(e) => {
                    handleNavClick(e, '/farmvest/roster');
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <ClipboardList size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Roster</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="EMPLOYEES">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-employees' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Employees" onClick={(e) => { handleNavClick(e, '/farmvest/employees'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Users size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Employees</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <li>
              {/* <button className={`${NAV_ITEM_BASE} ${activeTab === 'onboarding-process' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} onClick={(e) => { handleNavClick(e, '/farmvest/onboarding-process'); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <ShieldCheck size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Onboarding Process</span>
                </div>
              </button> */}
            </li>
            <NavGuard screenKey="ANIMAL_ONBOARDING">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'animal-onboarding' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Animal Onboarding" onClick={(e) => { handleNavClick(e, '/farmvest/animal-onboarding'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <CirclePlus size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Animal Onboarding</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="UNALLOCATED_ANIMALS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'unallocated-animals' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Unallocated Animals" onClick={(e) => { handleNavClick(e, '/farmvest/unallocated-animals'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <LayoutGrid size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Unallocated Animals</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="WALLET">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-wallet' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Wallet" onClick={(e) => { handleNavClick(e, '/farmvest/wallet'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Wallet size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Wallet</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="REVENUE">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-due-payouts' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Due Payouts" onClick={(e) => { handleNavClick(e, '/farmvest/due-payouts'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <TrendingUp size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Due Payouts</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="INVESTORS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-investors' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Unit Owners" onClick={(e) => { handleNavClick(e, '/farmvest/investors'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Briefcase size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Unit Owners</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="BUFFALO">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-buffalo' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Buffalo" onClick={(e) => { handleNavClick(e, '/farmvest/buffalo'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <PawPrint size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Buffalo</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="PAYMENTS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-payments' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Payments" onClick={(e) => { handleNavClick(e, '/farmvest/payments'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <CreditCard size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Payments</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="INVESTOR_ONBOARDING">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'investor-onboarding' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Investor Onboarding" onClick={(e) => { handleNavClick(e, '/farmvest/investor-onboarding'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <UserPlus size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Owner Onboarding</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="FARM_VISIT">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-farm-visit' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Farm Visit" onClick={(e) => { handleNavClick(e, '/farmvest/farm-visit'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Calendar size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Farm Visit</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <li className="mt-2 pt-2 border-t border-white/10">
              <span className="block pt-4 px-4 pb-1.5 text-[0.625rem] font-bold tracking-widest text-white/80 uppercase 4xl:pt-[48px] 4xl:px-[40px] 4xl:pb-[16px] 4xl:text-[1.4rem] 4xl:tracking-[0.12em]">Operations</span>
            </li>

            <NavGuard screenKey="INVENTORY">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-inventory' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Inventory" onClick={(e) => { handleNavClick(e, '/farmvest/inventory'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Package size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Inventory</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="TICKETS">
              <li>
                <button
                  className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-tickets' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`}
                  data-label="Ticket List"
                  onClick={(e) => {
                    handleNavClick(e, '/farmvest/tickets');
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <AlertCircle size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Ticket List</span>
                  </div>
                </button>
                {/* <button
                className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-tickets' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''} 4xl:text-4xl 4xl:py-10`}
                data-label="Tickets"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isSidebarOpen) { navigate('/farmvest/tickets'); return; }
                  setIsTicketsSubmenuOpen(!isTicketsSubmenuOpen);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <AlertCircle size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Tickets</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${isTicketsSubmenuOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {isTicketsSubmenuOpen && (
                <ul className="list-none pl-10 pr-0 my-0 flex flex-col gap-0.5 bg-black/10 rounded-b-[10px] -mt-0.5">
                   <li className="submenu-item-container">
                    <button
                      className={`flex items-center w-full py-2 px-3 border-none bg-transparent rounded-md text-white/60 text-[0.75rem] font-medium cursor-pointer transition-all duration-200 text-left hover:text-white hover:bg-white/5 ${activeTab === 'farmvest-tickets' && !location.search.includes('view=AI_ENTRY') ? 'text-[#f59e0b] font-bold' : ''}`}
                      onClick={(e) => { handleNavClick(e, '/farmvest/tickets'); }}
                    >
                      <span className="submenu-text">Ticket List</span>
                    </button>
                  </li>
                  <li className="submenu-item-container">
                    <button
                      className={`flex items-center w-full py-2 px-3 border-none bg-transparent rounded-md text-white/60 text-[0.75rem] font-medium cursor-pointer transition-all duration-200 text-left hover:text-white hover:bg-white/5 ${location.search.includes('view=AI_ENTRY') ? 'text-[#f59e0b] font-bold' : ''}`}
                      onClick={(e) => { handleNavClick(e, '/farmvest/tickets?view=AI_ENTRY'); }}
                    >
                      <span className="submenu-text">AI Entry</span>
                    </button>
                  </li>
                </ul>
              )} */}
              </li>
            </NavGuard>

            <NavGuard screenKey="MILK_PRODUCTION">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-milk' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Milk Production" onClick={(e) => { handleNavClick(e, '/farmvest/milk-production'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Milk size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Milk Production</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            {/* <li>
              <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-alerts' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} onClick={(e) => { handleNavClick(e, '/farmvest/alerts'); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Bell size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Alerts Management</span>
                </div>
              </button>
            </li> */}

            <NavGuard screenKey="IOT_ALERTS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'iot-alerts' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="IoT Alerts" onClick={(e) => { handleNavClick(e, '/farmvest/iot-alerts'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Cpu size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>IoT Alerts</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="LEAVE_REQUESTS">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-leave' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Leave Requests" onClick={(e) => { handleNavClick(e, '/farmvest/leave-requests'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <Calendar size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Leave Requests</span>
                  </div>
                </button>
              </li>
            </NavGuard>

            <NavGuard screenKey="UNIT_TRANSFER">
              <li>
                <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-unit-transfers' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Unit Transfers" onClick={(e) => { handleNavClick(e, '/farmvest/unit-transfer-requests'); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <ArrowLeftRight size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                    <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Unit Transfers</span>
                  </div>
                </button>
              </li>
            </NavGuard>


            {/* <li>
              <button className={`${NAV_ITEM_BASE} ${activeTab === 'farmvest-activation' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="User Activation" onClick={(e) => { handleNavClick(e, '/farmvest/user-activation'); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <UserCheck size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>User Activation</span>
                </div>
              </button>
            </li> */}
            {/* <li>
              <button className={`${NAV_ITEM_BASE} ${activeTab === 'account-deletion' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Account Deletion" onClick={(e) => { handleNavClick(e, '/farmvest/account-deletion'); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Trash2 size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Account Deletion</span>
                </div>
              </button>
            </li> */}
            <li>
              <button className={`${NAV_ITEM_BASE} ${activeTab === 'support' ? NAV_ITEM_ACTIVE : ''} ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Support Ticket" onClick={(e) => { handleNavClick(e, '/support'); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Mail size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Support Ticket</span>
                </div>
              </button>
            </li>
          </ul>

          {hasSession && (
            <div
              className="mt-auto p-3 border-t border-white/10"
              onMouseOver={(e) => {
                if (isSidebarOpen) return;
                const item = (e.target as HTMLElement).closest('.nav-item') as HTMLElement;
                if (item) {
                  const rect = item.getBoundingClientRect();
                  item.style.setProperty('--tooltip-top', `${rect.top + rect.height / 2}px`);
                }
              }}
            >
              <button className={`${NAV_ITEM_BASE} text-slate-300 hover:text-red-500 hover:bg-red-500/10 ${!isSidebarOpen ? NAV_ITEM_COLLAPSED : ''}`} data-label="Logout" onClick={(e) => { e.stopPropagation(); setIsLogoutModalOpen(true); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <LogOut size={18} className="4xl:w-[48px] 4xl:h-[48px]" />
                  <span className={`ml-2.5 4xl:ml-6 flex-1 whitespace-nowrap overflow-hidden truncate ${isSidebarOpen ? 'block' : 'md:hidden'}`}>Logout</span>
                </div>
              </button>
            </div>
          )}
        </nav>

        <main
          className="flex-1 overflow-y-auto flex flex-col p-2 md:p-2 bg-[#f8fafc] max-md:overflow-y-auto max-md:p-4 scrollbar-hide 4xl:py-2.5 4xl:px-4"
          style={{
            overflowY: [
              '/farmvest/roster',
              '/farmvest/employees',
              '/farmvest/buffalo',
              '/farmvest/tickets',
              '/farmvest/unit-transfer-requests'
            ].some(path => location.pathname === path)
              ? (window.innerWidth <= 1024 ? 'auto' : 'hidden')
              : 'auto',
            padding: ['/farmvest/user-activation', '/farmvest/wallet', '/farmvest/due-payouts'].includes(location.pathname) ? '0' : undefined,
            background: location.pathname === '/farmvest/user-activation' ? 'linear-gradient(to bottom right, #f0fdf4, #dcfce7)' : undefined
          }}
        >
          {children}
        </main>

      </div >

      {
        hasSession && (
          <>
            <ImageNamesModal />
            <AdminDetailsModal
              adminName={displayAdminName}
              adminMobile={adminMobile}
              adminRole={adminRole}
              profileImageSrc={profileImageSrc}
              lastLogin={lastLogin}
              presentLogin={presentLogin}
              onLogout={() => {
                dispatch(setShowAdminDetails(false));
                setIsLogoutModalOpen(true);
              }}
            />
            <RejectionModal />
            <ApprovalModal />
            <LogoutModal isOpen={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} onConfirm={onLogout!} />
            <Snackbar
              message={snackbar.message}
              type={snackbar.type as 'success' | 'error' | null}
              onClose={() => dispatch(setSnackbar({ message: null, type: null }))}
              duration={6000}
            />
            <NotificationSidebar
              isOpen={isNotificationSidebarOpen}
              onClose={() => setIsNotificationSidebarOpen(false)}
              onAllRead={() => dispatch(markAllRead())}
            />
            <TicketNotificationToast />
          </>
        )
      }

      {/* Snackbar is handled by uiSlice state */}
    </div >
  );
};

export default UserTabs;